import { compareValues } from "../../../utils/multiColumnSorting";

/**
 * A section's sort keys, one per active grouping level (in the order of *Daily printout grouping*).
 *
 * Per mapping §5.7: each level contributes a value that `compareValues` knows how to compare.
 * For Location/Floor/Role/Job, the value is a string. For Shift Time, the value is the **canonical
 * window's DateTime** (mapping §6.3) — never an arbitrary first-row timestamp — or, with custom shift
 * time groups on, the group's position in its already-ordered list. The group-forming
 * phase owns picking the canonical DateTime; this module just consumes the pre-extracted keys.
 */
export type SectionSortKeys = ReadonlyArray<string | number | import("luxon").DateTime>;

/** Per-level direction multiplier (`1` for asc, `-1` for desc). Aligned with each section level. */
export type SectionDirections = ReadonlyArray<1 | -1>;

/**
 * Compare two sections level-by-level using the shared `compareValues`.
 *
 * Reuses the same `customStringOrderMap` plumbing as row sort — there is only one value
 * comparator in the pipeline (mapping §5.8). When `directions` is supplied, each level's
 * comparison is multiplied by the corresponding direction so that user-configured `sortBy`
 * directions propagate to section ordering (e.g. `location desc` flips the section order when
 * Location is one of the grouping levels). Missing direction entries default to ascending.
 *
 * Returns the first non-zero result; zero if all levels tie (Array.prototype.sort then
 * preserves input order, which is by §6.3 buckets' encounter order).
 */
export const compareSections = (
	customStringOrderMap: Map<string, number> | null | undefined,
	directions: SectionDirections = [],
): ((aKeys: SectionSortKeys, bKeys: SectionSortKeys) => number) => {
	return (aKeys, bKeys) => {
		const len = Math.min(aKeys.length, bKeys.length);
		for (let i = 0; i < len; i++) {
			const cmp = compareValues(aKeys[i], bKeys[i], customStringOrderMap);
			if (cmp !== 0) return (directions[i] ?? 1) * cmp;
		}
		return 0;
	};
};
